import { httpsCallable } from 'firebase/functions';
import {
  collection, onSnapshot,
  doc, updateDoc, arrayUnion, setDoc, deleteDoc, addDoc,
  runTransaction, query, orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db, functions } from '../firebase';

const sendAdvisorInviteFn    = httpsCallable(functions, 'sendAdvisorInvite');
const acceptAdvisorRequestFn = httpsCallable(functions, 'acceptAdvisorRequest');
const revokeAdvisorFn        = httpsCallable(functions, 'revokeAdvisor');
const suggestFactFn          = httpsCallable(functions, 'suggestFact');
const retractSuggestionFn    = httpsCallable(functions, 'retractSuggestion');

export const sendAdvisorInvite    = (data) => sendAdvisorInviteFn(data).then(r => r.data);
export const acceptAdvisorRequest = (requestId) => acceptAdvisorRequestFn({ requestId }).then(r => r.data);
export const revokeAdvisor        = (advisorUid) => revokeAdvisorFn({ advisorUid }).then(r => r.data);
export const suggestFact          = (clientUid, text, libraryItemId = null) => suggestFactFn({ clientUid, text, libraryItemId }).then(r => r.data);
export const retractSuggestion    = (clientUid, pendingFactId, sentSuggestionId) => retractSuggestionFn({ clientUid, pendingFactId, sentSuggestionId }).then(r => r.data);

// Deferred onSnapshot: delays subscription start to the next macrotask so that
// React StrictMode's synchronous effect-cleanup can cancel the timer before
// Firestore ever registers the listener. Without this, StrictMode's rapid
// mount→cleanup→remount cycle leaves a target in the watch stream mid-init
// (ve === -1), triggering Firestore's internal ca9 assertion.
function deferredSnapshot(ref, callback) {
  let unsub = null;
  const timer = setTimeout(() => {
    unsub = onSnapshot(ref, callback);
  }, 0);
  return () => {
    clearTimeout(timer);
    if (unsub) unsub();
  };
}

export function subscribeToAdvisors(uid, callback) {
  return deferredSnapshot(collection(db, `users/${uid}/advisors`), snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export function subscribeToClients(uid, callback) {
  return deferredSnapshot(collection(db, `users/${uid}/clients`), snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export function subscribeToAdvisorRequests(uid, callback) {
  return deferredSnapshot(collection(db, `users/${uid}/advisorRequests`), snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(d => d.status === 'pending'));
  });
}

export function subscribeToPendingFacts(uid, callback) {
  return deferredSnapshot(collection(db, `users/${uid}/pendingFacts`), snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(d => d.status === 'pending'));
  });
}

// Approve: use pendingFact.id as the memory fact id so retractSuggestion can
// find it by exact id match rather than text matching.
export async function approvePendingFact(uid, pendingFact) {
  const memRef     = doc(db, `users/${uid}/meta/agentMemory`);
  const pendingRef = doc(db, `users/${uid}/pendingFacts/${pendingFact.id}`);
  const now = new Date().toISOString();
  const fact = {
    id:             pendingFact.id,
    text:           pendingFact.text,
    createdAt:      now,
    lastAccessedAt: now,
    status:         'active',
    pinned:         false,
    source:         { type: 'advisor', name: pendingFact.fromAdvisorName, advisorUid: pendingFact.fromAdvisorUid },
  };
  await Promise.all([
    setDoc(memRef, { facts: arrayUnion(fact), updatedAt: serverTimestamp() }, { merge: true }),
    deleteDoc(pendingRef),
  ]);
}

export async function rejectPendingFact(uid, factId) {
  await updateDoc(doc(db, `users/${uid}/pendingFacts/${factId}`), { status: 'rejected' });
}

// Toggle a memory fact's enabled state. Uses a transaction since Firestore
// arrays require a read-modify-write cycle for in-place updates.
export async function toggleMemoryFact(uid, factId) {
  const memRef = doc(db, `users/${uid}/meta/agentMemory`);
  await runTransaction(db, async (txn) => {
    const snap = await txn.get(memRef);
    const facts = snap.exists() ? (snap.data().facts ?? []) : [];
    const idx = facts.findIndex(f => f.id === factId);
    if (idx === -1) return;
    const cur     = facts[idx];
    const updated = { ...cur, enabled: cur.enabled === false ? true : false };
    const newFacts = [...facts];
    newFacts[idx] = updated;
    txn.set(memRef, { facts: newFacts }, { merge: true });
  });
}

// ─── Suggestion Library ───────────────────────────────────────────────────────

export function subscribeToSuggestionLibrary(uid, callback) {
  return deferredSnapshot(
    query(collection(db, `users/${uid}/suggestionLibrary`), orderBy('createdAt', 'asc')),
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
}

export async function addToSuggestionLibrary(uid, text) {
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 128) throw new Error('Text must be 1–128 characters.');
  const now = new Date().toISOString();
  const ref = await addDoc(collection(db, `users/${uid}/suggestionLibrary`), {
    text: trimmed, createdAt: now, updatedAt: now,
  });
  return ref.id;
}

export async function updateSuggestionLibraryItem(uid, itemId, text) {
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 128) throw new Error('Text must be 1–128 characters.');
  await updateDoc(doc(db, `users/${uid}/suggestionLibrary/${itemId}`), {
    text: trimmed, updatedAt: new Date().toISOString(),
  });
}

export async function deleteFromSuggestionLibrary(uid, itemId) {
  await deleteDoc(doc(db, `users/${uid}/suggestionLibrary/${itemId}`));
}

// ─── Sent Suggestions ─────────────────────────────────────────────────────────

export function subscribeToSentSuggestions(uid, callback) {
  return deferredSnapshot(
    query(collection(db, `users/${uid}/sentSuggestions`), orderBy('sentAt', 'desc')),
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
}
